import type { Place } from '../types';
import { ASSURANCE, CERT_STATE, TYPE_LABEL, formatDate } from '../lib/trust';
import { TypeGlyph } from '../lib/glyphs';
import './PlaceRow.css';
import { useT } from '../i18n';

interface Props {
    place: Place;
    selected: boolean;
    onSelect: (place: Place) => void;
}

/** Metres → "180 m" / "2.4 km" */
export function formatDistance(m?: number): string {
    if (m == null) return '';
    return m < 950 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`;
}

/**
 * A result row.
 *
 * The left border weight encodes the strength of the halal claim — solid for a
 * certifier, dashed for community data. That is the same language the map pins
 * use, so the two surfaces teach each other.
 */
export function PlaceRow({ place, selected, onSelect }: Props) {
    const { t, lang } = useT();
    const assurance = place.halal ? ASSURANCE[place.halal.status] : null;
    const state = CERT_STATE[place.certState];
    const dead = place.certState === 'expired' || place.certState === 'delisted';

    return (
        <li>
            <button
                type="button"
                className={`row row--${assurance?.weight ?? 'none'} ${selected ? 'is-selected' : ''}`}
                onClick={() => onSelect(place)}
                aria-current={selected ? 'true' : undefined}
            >
                <span className="row__glyph" aria-hidden="true">
                    <TypeGlyph type={place.type} size={19} />
                </span>

                <span className="row__body">
                    <span className="row__title">
                        <span className="row__name">{place.name}</span>
                        {place.nameLocal && place.nameLocal !== place.name && (
                            <span className="row__name-local" lang="zh">{place.nameLocal}</span>
                        )}
                    </span>

                    <span className="row__meta">
                        <span>{t(TYPE_LABEL[place.type])}</span>
                        {place.cuisines?.length ? <span>· {place.cuisines.slice(0, 2).join(', ')}</span> : null}
                        {place.district ? <span>· {place.district}</span> : null}
                        {place.distance != null ? <span className="row__distance">· {formatDistance(place.distance)}</span> : null}
                    </span>

                    {assurance && (
                        <span className={`row__trust tone-${state.tone}`}>
                            <span className="row__trust-dot" aria-hidden="true" />
                            <span>{t(assurance.short)}</span>
                            {state.showsCertificate && state.label && (
                                <span className="row__trust-state">
                                    {dead
                                        ? `· ${t(state.label).toLowerCase()}${place.halal?.validUntil ? ` ${formatDate(place.halal.validUntil, lang.code)}` : ''}`
                                        : place.certState === 'expiring_soon' && place.halal?.validUntil
                                            ? `· ${t('cert.expiring_soon').toLowerCase()} ${formatDate(place.halal.validUntil, lang.code)}`
                                            : ''}
                                </span>
                            )}
                        </span>
                    )}
                </span>
            </button>
        </li>
    );
}
